import styled from '@emotion/styled/macro'
import { ClassNames, ClassNamesContent } from '@emotion/react'
import * as React from 'react'
import type { CSSProperties, ReactNode } from 'react'
import ReactModal from 'react-modal'

import { Icon } from '~/components/atoms/Icon'

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
ReactModal.setAppElement(document.getElementById('root')!)

export interface DialogProps {
  className?: string
  style?: CSSProperties

  opened: boolean

  children: ReactNode
  title: ReactNode

  variant?: 'dialog' | 'drawer'
  fullscreen?: boolean

  onClose(): void
}

export const Dialog = ({
  opened,
  children,
  title,
  variant = 'dialog',
  fullscreen = false,
  onClose,
  className,
  style
}: DialogProps) => {
  return (
    <ClassNames>
      {({ css }) => (
        <ReactModal
          className={{
            base: fullscreen
              ? fullscreenStyle(css)
              : variant === 'drawer'
              ? drawerStyle(css)
              : contentStyle(css),
            afterOpen: fullscreen
              ? fullscreenAfterOpenStyle(css)
              : contentAfterOpenStyle(css),
            beforeClose: fullscreen
              ? fullscreenBeforeCloseStyle(css)
              : contentBeforeCloseStyle(css)
          }}
          overlayClassName={{
            base: backdropStyle(css),
            afterOpen: backdropAfterOpenStyle(css),
            beforeClose: backdropBeforeCloseStyle(css)
          }}
          style={{
            content: style
          }}
          closeTimeoutMS={1000}
          isOpen={opened}
          onRequestClose={() => onClose()}
        >
          <Title>
            <span>{title}</span>
            <CloseButton
              title="閉じる"
              aria-label="ダイアログを閉じる"
              onClick={() => onClose()}
            >
              <Icon type="close" />
            </CloseButton>
          </Title>
          <Body className={className}>{children}</Body>
        </ReactModal>
      )}
    </ClassNames>
  )
}

const contentStyle = (css: ClassNamesContent['css']) => css`
  --_radius: 8px;
  --_center-transform: translate(-50%, -50%);

  position: fixed;
  top: 50%;
  left: 50%;
  min-width: 120px;
  max-width: calc(100vw - var(--gutter-medium) * 2);
  min-height: 120px;
  max-height: 90vh;
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;

  background-color: var(--gray-0);
  border-radius: var(--_radius);
  overflow: hidden;

  opacity: 0;
  transform: var(--_center-transform) translateY(300px);
`

const drawerStyle = (css: ClassNamesContent['css']) => css`
  ${contentStyle(css)}

  --_center-transform: translate(0px, 0px);

  top: 48px;
  margin: 0 auto;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  max-height: 100%;
  width: 100%;
  max-width: 960px;
  align-items: stretch;

  border-radius: var(--_radius) var(--_radius) 0 0;

  @media (max-height: 400px) {
    top: 12px;
  }
`

const contentAfterOpenStyle = (css: ClassNamesContent['css']) => css`
  opacity: 1;
  transform: var(--_center-transform) translateY(0px);
  transition: opacity 0.2s linear, transform 0.4s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 0.1s ease-out;
  }
`

const contentBeforeCloseStyle = (css: ClassNamesContent['css']) => css`
  pointer-events: none;

  opacity: 0;
  transform: var(--_center-transform) translateY(300px);
  transition: opacity 0.1s ease-in, transform 0.1s 0.1s ease-in;

  @media (prefers-reduced-motion: reduce) {
    transform: var(--_center-transform);
    transition: opacity 0.1s ease-in;
  }
`

const backdropStyle = (css: ClassNamesContent['css']) => css`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  background-color: hsla(0, 0%, 0%, 0.5);

  transition: opacity 0.2s ease-out;
`

const backdropAfterOpenStyle = (css: ClassNamesContent['css']) => css`
  opacity: 1;
`

const backdropBeforeCloseStyle = (css: ClassNamesContent['css']) => css`
  pointer-events: none;

  opacity: 0;
`

const fullscreenStyle = (css: ClassNamesContent['css']) => css`
  position: fixed;
  top: 16px;
  left: 16px;
  right: 16px;
  bottom: 16px;
  width: calc(100vw - 32px);
  height: calc(100vh - 32px);
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  align-items: stretch;

  background-color: var(--gray-0);
  border-radius: 8px;
  overflow: hidden;

  opacity: 0;
  transform: translateX(0) translateY(0) !important;

  /* Mobile fullscreen - use 414px breakpoint from DeviceSize.MOBILE */
  @media (max-width: 414px) {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }

  /* Support for devices with safe areas (notches, etc.) */
  @supports (padding: env(safe-area-inset-top)) {
    @media (max-width: 414px) {
      padding-top: env(safe-area-inset-top);
      padding-right: env(safe-area-inset-right);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
    }
  }
`

const fullscreenAfterOpenStyle = (css: ClassNamesContent['css']) => css`
  opacity: 1;
  transform: translateX(0) translateY(0) !important;
`

const fullscreenBeforeCloseStyle = (css: ClassNamesContent['css']) => css`
  opacity: 0;
`

const Title = styled.div`
  --_button-padding: var(--gutter-small);
  --_button-margin: var(--gutter-extra-small);
  --_button-size: 16px;

  position: relative;
  padding: var(--gutter-small)
    calc(16px + var(--_button-padding) * 2 + var(--_button-margin));
  font-size: 14px;
  line-height: 1.71;
  border-bottom: 1px solid var(--gray-400);

  text-align: center;
`

const CloseButton = styled.button`
  position: absolute;
  right: var(--_button-margin);
  top: 50%;
  font-size: var(--_button-size);
  padding: var(--_button-padding);
  display: inline-flex;
  justify-content: center;
  align-items: center;

  border-radius: 4px;

  transform: translateY(-50%);

  &:hover,
  &:focus-visible {
    background-color: var(--gray-200);
  }
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
`
